import store from "../store/store";
import * as TaskActions from "../store/actions/taskActions";
import newHRTimestamp from "../lib/picos/timestamp";
import OptionHelper from "../Configuration/GeneralOptions/OptionHelper";
import find from "../store/find";
import {roundDurationToMinutes} from "../lib/picos/duration";
import {getCurrentUserId} from "../lib/picos/currentLogin";
import {AWAITING_TITLE, getBillableRate, getRelatedMatters} from "./TaskHelper";
import {uuid} from "../@types/schema";
import Task, {AssignedToType} from "../entities/task";
import Invoice from "../entities/invoice";
import {RoundingMethods} from "../Configuration/GeneralOptions/OptionConstants";
import User from "../entities/user";
import {RestoreStatus, RestoreStatusType} from "../store/actions/miscActions";

/**
 * OWA-7 stale-draft guard: is the live (Redux) task newer than a recovered
 * autosave draft of the same task?
 *
 * Crash-recovery re-opens cached task drafts on mount. A draft captured with
 * running=true can outlive the live task being stopped/edited (e.g. overnight),
 * and re-opening + saving it would resurrect the timer. We compare the server
 * timestamp `dbAddedOn` first (the canonical "live is newer" signal, same idiom
 * as InvoiceCard/ContactCard), falling back to `addedOn` when `dbAddedOn` is not
 * yet populated on one side. Returns true ONLY when the live task is strictly
 * newer than the draft — a draft that is newer-or-equal must still be recovered.
 *
 * @param {Task} live - the live task from Redux/props
 * @param {Task} draft - the recovered autosave draft
 * @returns {boolean} true if the live task is strictly newer than the draft
 */
export const isLiveTaskNewer = (live: Task, draft: Task): boolean => {
    if (!live || !draft) return false;

    const liveDb = live.dbAddedOn ?? null;
    const draftDb = draft.dbAddedOn ?? null;
    if (liveDb != null && draftDb != null) return liveDb > draftDb;

    // Fall back to addedOn when dbAddedOn is missing on either side.
    const liveAdded = live.addedOn ?? 0;
    const draftAdded = draft.addedOn ?? 0;
    return liveAdded > draftAdded;
};

/**
 * OWA-7 stale-draft recovery guard. Given the live-task index (props.tasks) and
 * a recovered autosave draft, returns true when the draft is STALE and must NOT
 * be re-opened — i.e. a live task with the same id exists AND is strictly newer
 * than the draft. Used identically by the crash-recovery effects in TaskGrid and
 * AssociatedTasks. A draft with no live counterpart, or one that is
 * newer-or-equal to the live task, is NOT stale (it is still recovered).
 *
 * @param {{get: (id: string) => Task | undefined} | null | undefined} liveTasks - the live-task index (props.tasks)
 * @param {Task} draft - the recovered autosave draft
 * @returns {boolean} true if the draft is stale and should be skipped
 */
export const isStaleDraft = (
    liveTasks: {get: (id: string) => Task | null | undefined} | null | undefined,
    draft: Task,
): boolean => {
    const live = liveTasks?.get(draft.id);
    return !!live && isLiveTaskNewer(live, draft);
};

/**
 * OWA-525 crash-recovery gate: is the IndexedDB backup restore complete?
 *
 * Draft crash-recovery (TaskGrid / AssociatedTasks) leans on isStaleDraft to
 * reject a stale running draft — but isStaleDraft can only do that once the
 * LIVE task is present in the index it is handed. That index is populated by
 * `restore._loadBackups`, which dispatches `setRestoreStatus(Restored)` only
 * AFTER every entity (tasks included) has been loaded into Redux. If recovery
 * runs before then, the index is empty, isStaleDraft returns false (no live
 * counterpart → "not stale, recover it"), and a stale running draft is
 * re-opened; saving it re-runs stopTimer against a stale lastStart, inflating
 * elapsedTime and clearing completionDate. So both components must defer
 * recovery until this returns true, at which point isStaleDraft is meaningful.
 *
 * @param {RestoreStatusType} restoreStatus - misc.restoreStatus from Redux
 * @returns {boolean} true once backups have been restored into Redux
 */
export const isRestoreComplete = (restoreStatus: RestoreStatusType): boolean =>
    restoreStatus === RestoreStatus.Restored;

/**
 * Start up the task timer.  Start by stopping any other timers for the user.
 *
 * @param {Task} task - the task to start
 * @param {uuid} [userId] - optional id of the user that started the task
 * @return {Task}
 */
export const startTimer = (task: Task, userId?: uuid): Task => {
    if (task.status === 'D') {
        console.warn('startTimer: Ignoring deleted task', task.id);
        return task;
    }
    if (!userId) userId = getCurrentUserId();

    const taskClone = task.clone();

    // Stop all existing timers
    const tasks = store.getState().tasks;

    // Check for running timers started BY THIS USER.
    // Stop them and commit their times.
    tasks.forEach(t => {
        // Not this task, started by this user, and is running
        if (t.running &&
            (t.id !== taskClone.id) &&
            (t.assignedTo === userId || (!t.assignedTo && t.addedBy === userId))
        ) {
            // Stop the timer and record the values
            stopTimer(t, userId);
        }
    });

    // Start up this task

    // If this task has not yet been assigned to a user (but to a group), collapse the field to the current user
    if (taskClone.assignedToType === AssignedToType.Group) {
        taskClone.assignedTo = userId;
        taskClone.assignedToType = AssignedToType.User;
    }

    // Get the now current billing rate and set it for laborRate
    taskClone.running = true;
    taskClone.lastStart = Date.now();
    taskClone.assignedToType = AssignedToType.User;
    taskClone.assignedTo = getCurrentUserId();
    taskClone.addedBy = userId;
    taskClone.addedOn = newHRTimestamp();

    // Set the billable rate and start time if this is the first time added
    if (taskClone.elapsedTime === 0) {
        // Start time is the first time this task was ever started (a Date). lastStart is the time in millis the
        // task was last started.
        taskClone.startTime = new Date();
        taskClone.laborRate = getBillableRate(taskClone.assignedTo, getRelatedMatters(taskClone)[0]);
    }

    // Always need a title
    if (!taskClone.title) taskClone.title = AWAITING_TITLE;
    store.dispatch(TaskActions.saveTaskQueue(taskClone));

    // Send back the clone
    return taskClone;
};

/**
 * Stop a running timer.
 *
 * @param {Task} task - the task stop
 * @param {uuid} [userId] - optional id of the user that stopped the task
 * @param {boolean} noSave
 * @returns {Task}
 */
export const stopTimer = (task: Task, userId?: uuid, noSave: boolean = false): Task => {
    if (task.status === 'D') {
        console.warn('stopTimer: Ignoring deleted task', task.id);
        return task;
    }
    if (!userId) userId = getCurrentUserId();

    const taskClone = task.clone();

    // Stop the task if it is running
    if (taskClone.running) {
        // Make sure we are not adding to zero, or we get a ridiculous time in the timer.
        if (!taskClone.lastStart) {
            console.warn('stopTimer: Stopping a timer with no lastStart.', task.id);
        }
        else {
            const delta = Date.now() - taskClone.lastStart;
            const MAX_TIMER_DELTA = 24 * 60 * 60 * 1000; // 24 hours
            if (delta > MAX_TIMER_DELTA) {
                console.warn(`stopTimer: stale timer detected. Task ${task.id}, delta=${delta}ms (${(delta / 3600000).toFixed(1)}h). Capping at 24h.`, new Error('stopTimer stale').stack);
                taskClone.elapsedTime = (taskClone.elapsedTime || 0) + MAX_TIMER_DELTA;
            } else if (delta < 0) {
                console.warn(`stopTimer: negative delta detected. Task ${task.id}, delta=${delta}ms. Ignoring.`);
            } else {
                taskClone.elapsedTime = (taskClone.elapsedTime || 0) + delta;
            }
            console.debug(`TIMER_STOP: task=${task.id}, prev=${task.elapsedTime}, delta=${delta}ms, new=${taskClone.elapsedTime}, running=${task.running}, lastStart=${task.lastStart}`, new Error('stopTimer trace').stack);
        }
    }

    taskClone.lastStart = 0.0;
    taskClone.running = false;
    taskClone.addedBy = userId;
    taskClone.addedOn = newHRTimestamp();

    // Always need a title
    if (!task.title) taskClone.title = AWAITING_TITLE;

    // Recompute cost
    const updatedTask = updateCost(taskClone);

    // Save the task
    if (!noSave) store.dispatch(TaskActions.saveTaskQueue(updatedTask));

    return updatedTask;
};

/**
 * Update the activity cost based on all the parameters.
 *
 * @param {Task} task - task to compute (immutable, always returns clone)
 * @param {object} [toUpdate] - object with {fieldName: value} pairs
 * @param {boolean} [forceTotals] - always calculate cost, even for flat fees
 * @return {Task}
 */
export const updateCost = (task: Task, toUpdate?: Partial<Task>, forceTotals?: boolean): Task => {
    let taskClone;
    let hasCloned = false;

    function clone(current: Task) {
        if (!hasCloned) {
            hasCloned = true;
            return current.clone();
        }
        else return current;
    }

    if (toUpdate) {
        // Check each of the update fields for parity, if any is different, we update with a clone.
        const diffs = (Object.keys(toUpdate) as Array<keyof Task>).some(key => {
            return task[key] !== toUpdate[key];
        });

        if (diffs) {
            taskClone = Object.assign(clone(task), toUpdate);
        }
        else taskClone = task;
    }
    else taskClone = task;

    const persisted = taskClone.persisted;

    // Determine the total cost, if billable
    if (taskClone.billable && taskClone.status !== 'D') {
        const invoice = taskClone.invoiceId ? find('invoices', taskClone.invoiceId) as Invoice : null;

        const billingIncrement = invoice?.billingIncrement || OptionHelper.getOptionValueStatic(OptionHelper.BILLING_INCREMENT) as number || 6;
        const roundingMethod = invoice?.roundingMethod || OptionHelper.getOptionValueStatic(OptionHelper.ROUNDING_METHOD) as string || RoundingMethods.ROUNDING_METHOD_UP;

        // Ensure that the travelTime and laborTime add up to the elapsedTime
        const elapsedTimeArg = (taskClone.elapsedTime || 0) + (taskClone.running ? Date.now() - (taskClone.lastStart || 0) : 0);
        const elapsedTime = roundDurationToMinutes(elapsedTimeArg, billingIncrement, roundingMethod);

        // Travel time can not be larger than elapsed time
        const travelTimeArg = Math.min(elapsedTime, (taskClone.isTravelTimeOverridden ? taskClone.travelTimeOverride : taskClone.travelTime) || 0);
        const travelTime = roundDurationToMinutes(travelTimeArg, billingIncrement, roundingMethod);

        // Labor time is whatever is left after travel time
        const laborTimeArg = taskClone.isLaborTimeOverridden ? taskClone.laborTimeOverride : (elapsedTime - travelTime);
        const laborTime = roundDurationToMinutes(laborTimeArg, billingIncrement, roundingMethod);

        // Get labor and travel rates
        const currentUser = find('users', getCurrentUserId()) as User;

        const laborRate = taskClone.isLaborRateOverridden
                          ? taskClone.laborRateOverride || 0
                          : taskClone.laborRate || currentUser?.billableRate || 0;

        const travelRate = taskClone.isTravelRateOverridden
                           ? taskClone.travelRateOverride || 0
                           : taskClone.travelRate || currentUser?.billableRate || 0;

        // Set the travel and labor time on the activity
        if (task.laborTime !== laborTime) clone(taskClone).laborTime = laborTime;

        // Was the cost overridden, a flat fee or a no charge?
        if ((!forceTotals && taskClone.flatFeeBucket) || taskClone.noCharge || taskClone.hideFromInvoice) {
            if (task.cost !== 0) clone(taskClone).cost = 0;
        }
        // else if(task.isCostOverridden) task.cost = task.costOverride;
        else {
            // Calculate the total cost. Don't forget to use fractional hours if requested in
            // console.log(`task.cost = (${laborRate || 0} * ${laborTime / 3600000 || 0}) + (${travelRate || 0} * ${travelTime / 3600000 || 0})`);
            const cost = ((laborRate || 0) * (laborTime / 3600000 || 0)) + ((travelRate || 0) * (travelTime / 3600000 || 0));
            if (task.cost !== cost) clone(taskClone).cost = cost;
        }
    }

    if (hasCloned) {
        if (taskClone.persisted !== persisted) taskClone.persisted = persisted;
    }

    return taskClone;
};